"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { ChannelProvider, useChannel } from "ably/react";
import { getOrgRolesChannel, getTeamRolesChannel } from "@/constants/Ably/channels";
import { EVENT_MSG_ROLE_MEMBERSHIP_CHANGED } from "@/constants/Ably/events";
import { createUrlQueryPredicate } from "@/constants/Ably/queryPredicates";
import { useTeamsContext } from "@/hooks/useTeamsContext";
const ROLE_HOLDERS_URL = "/api/v1/principal-roles/role-holders";
// The workflow table's ball-in-court column and its unstaffed-role warning are both computed from
// role membership server-side, so they go stale alongside the holder reads.
const WORKFLOW_APPLICATIONS_URL = "/api/v1/workflow-applications";

// The files/projects column keys its own query by hand rather than through the generated client,
// so the url predicate alone would leave it cached (same carve-out as WorkflowsRealtimeSubscriber).
const WORKFLOW_APPLICATIONS_BY_SCOPE_KEY = "workflow-applications";
const matchesUrl = createUrlQueryPredicate([ROLE_HOLDERS_URL, WORKFLOW_APPLICATIONS_URL]);
const matchesRoleDerivedQuery = (query: {
  queryKey: readonly unknown[];
}) => matchesUrl(query) || query.queryKey[0] === WORKFLOW_APPLICATIONS_BY_SCOPE_KEY;

/**
 * Keeps role-derived surfaces live: the pickers' role-holder counts, the unstaffed-step warnings
 * built on them, and the workflow table's ball-in-court column.
 *
 * Nothing else covers it. The global `MutationCache` only invalidates in the tab that made the
 * edit, and the app runs with `refetchOnWindowFocus` off — so a picker went on reporting a role as
 * empty long after somebody staffed it. Covers other tabs, other users, and a panel left open
 * across the change.
 *
 * Two channels: the team's for grants at the team or below, the organization's for org-level grants
 * every team inherits.
 */
export function RoleMembershipSubscriber() {
  const $ = _c(7);
  const {
    currentScopeId,
    organizationScopeId
  } = useTeamsContext();
  let t0;
  if ($[0] !== currentScopeId) {
    t0 = currentScopeId ? <RoleChannelListener channelName={getTeamRolesChannel(currentScopeId)} /> : null;
    $[0] = currentScopeId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== organizationScopeId) {
    t1 = organizationScopeId ? <RoleChannelListener channelName={getOrgRolesChannel(organizationScopeId)} /> : null;
    $[2] = organizationScopeId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = <>{t0}{t1}</>;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// Outer gate / inner listener split (see RoleChangeSubscriber): `useChannel` throws without a
// `ChannelProvider` above it, so the provider cannot be conditional in the same component.
function RoleChannelListener(t0) {
  const $ = _c(5);
  const {
    channelName
  } = t0;
  let t1;
  if ($[0] !== channelName) {
    t1 = <RoleChannelInvalidator channelName={channelName} />;
    $[0] = channelName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== channelName || $[3] !== t1) {
    t2 = <ChannelProvider channelName={channelName}>{t1}</ChannelProvider>;
    $[2] = channelName;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function RoleChannelInvalidator(t0) {
  const $ = _c(4);
  const {
    channelName
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== channelName) {
    t1 = {
      channelName
    };
    $[0] = channelName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        predicate: matchesRoleDerivedQuery
      });
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useChannel(t1, EVENT_MSG_ROLE_MEMBERSHIP_CHANGED, t2);
  return null;
}